import clsx from 'clsx'
import React, {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'

import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'

import DraggablePaper from 'bpm/components/sortable/DraggablePaper'
import ConfirmationDialog from 'bpm/pages/CompanyAccountsSettingsPage/components/ConfirmationDialog'
import { AutoCompleteInput } from 'bpm/pages/ProductsPage/AddProductDialog/AutoCompleteInput'; // TODO move this to common component
import EditableTextBlock, {
    IEditableContentInfo,
    countPlaintextChars,
} from 'bpm/pages/ProductsPage/CompanyPage/components/EditableTextBlock'; // move this to common component
import { useNoteReasonsStore } from 'bpm/pages/ProductsPage/store'
import {
    LABEL_REASON_NOTE,
    TITLE_DELTEK_NOTE,
    VARIABLE_NOTES_TITLE,
    cancelAddNoteWarning,
    noteReasonLabels,
    titleHeaderAction,
} from './ListingNotes.constants'
import { NoteType, ProductDetailNoteData } from './ListingNotes.types'
import PDNColors from './PDNColors'

const { deltekNoteColor, deltekNoteHoverColor, viewOnlyBorderColor } = PDNColors

export interface AddNotesModalProps {
    open: boolean
    isEditMode: boolean
    title?: string
    reasonOptionLabel?: string
    reasonOptions?: string[]
    reasonNoteLabel?: string
    onSave: ({
        noteType,
        longDescription,
        isEdit,
        isTemp,
        originalNote,
        reason,
    }: {
        noteType: NoteType
        longDescription: string
        isEdit: boolean
        isTemp: boolean
        originalNote?: Partial<ProductDetailNoteData>
        reason: string
    }) => void
    onCancel: () => void
    note?: Partial<ProductDetailNoteData>
    noteType?: NoteType
    action?: keyof typeof noteReasonLabels | ''
    resetAction?: () => void
}

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paperScrollPaper': {
            padding: theme.spacing(1.5),
        },
    },
    dialogTitle: {
        paddingRight: theme.spacing(2),
        '& .MuiTypography-h6': {
            fontSize: pxToRem(24),
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
    },
    dialogContent: {
        width: '35vw',
    },
    primaryButton: {
        backgroundColor: deltekNoteColor,
        color: theme.palette.common.white,
        '&:hover': {
            backgroundColor: deltekNoteHoverColor,
        },
    },
    secondaryButton: {
        color: deltekNoteColor,
        '&:hover': {
            backgroundColor: theme.palette.common.white,
        },
    },
    button: {
        width: 'auto',
        minWidth: pxToRem(100),
        height: pxToRem(41),
        borderRadius: 100,
    },
    disabledButton: {
        backgroundColor: viewOnlyBorderColor,
        '&.MuiButton-root.Mui-disabled': {
            color: theme.palette.common.white,
        },
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(2.5),
        },
    },
    elementContainer: {
        flexWrap: 'nowrap',
        overflowY: 'auto',
        minHeight: 200,
        height: 'calc(100%)',
    },
    rteInput: {
        maxHeight: '50vh',
    },
    reasonLabel: {
        fontSize: pxToRem(14),
        display: 'block',
        position: 'relative',
        '&.MuiGrid-grid-xs-12': {
            padding: theme.spacing(0, 1),
            bottom: '-4px',
        },
    },
    required: {
        color: theme.palette.error.main,
    },
}))

export const AddNotesModal: React.FC<AddNotesModalProps> = ({
    open,
    isEditMode,
    title,
    onSave,
    onCancel,
    note,
    noteType = NoteType.GeneralNote,
    action,
    resetAction,
}) => {
    const textLimit = noteType === NoteType.CopyMove ? 3800 : 4000
    const classes = useStyles({ isEditMode })
    const reasons = useNoteReasonsStore((state) => state.reasons)

    const reasonOptions = useMemo(() => {
        return (reasons as Record<NoteType, string[]>)[noteType] || []
    }, [reasons, noteType])

    const headerTitle = useMemo(() => {
        if (!!action) {
            return VARIABLE_NOTES_TITLE(titleHeaderAction[action])
        }
        return TITLE_DELTEK_NOTE
    }, [
        title,
        action,
        titleHeaderAction,
        VARIABLE_NOTES_TITLE,
        TITLE_DELTEK_NOTE,
    ])

    const [longDescription, setLongDescription] = useState('')
    const [postFix, setPostFix] = useState('')
    const [noteReason, setNoteReason] = useState<string | undefined>()
    const [isReasonValid, setIsReasonValid] = useState(true)

    const [error, setError] = useState('')

    const [isBlockCancelOpen, setIsBlockCancelOpen] = useState(false)

    const handleNoteInputChange = useCallback(
        (newContent: IEditableContentInfo) => {
            if (!!error) {
                setError('')
            }
            if (newContent.characterCount > textLimit) {
                setError(`Maximum characters allowed is ${textLimit}`)
            }

            setLongDescription(newContent.content)
        },
        [textLimit, error]
    )

    const resetStates = useCallback(() => {
        setLongDescription('')
        setNoteReason(undefined)
        setError('')
        setIsReasonValid(true)
        setPostFix('')
    }, [])

    const handleClose = useCallback(() => {
        if (!!resetAction && (!!longDescription || !noteReason)) {
            setIsBlockCancelOpen(true)
            return
        }
        resetStates()
        onCancel?.()
    }, [resetAction, longDescription, onCancel, resetStates])

    const handleSave = useCallback(() => {
        if (!!error) {
            return
        }


        if (!longDescription || !countPlaintextChars(longDescription)) {
            setError('Please add a note')
            return
        }

        if (!!action && !noteReason) {
            setIsReasonValid(false)
            return
        }

        let temp = {}
        const isTemporary = !!note?.isTemporary

        if (!!note) {
            temp = { ...note, noteId: note.id }
        }

        onSave({
            ...temp,
            isTemp: isTemporary,
            noteType,
            longDescription: `${longDescription}${postFix}`,
            isEdit: !!note,
            reason: noteReason ?? 'General Note',
        })

        resetStates()
        onCancel?.()
    }, [action, longDescription, noteReason, handleClose, resetStates, error, onCancel, note, noteType, postFix, onSave])

    const handleBlockModalClose = useCallback(() => {
        setIsBlockCancelOpen(false)
    }, [])

    const handleBlockModalProceed = useCallback(() => {
        resetAction?.()
        resetStates()
        onCancel?.()
        setIsBlockCancelOpen(false)
    }, [resetAction, resetStates])

    const handleSetReason = useCallback((e) => {
        setIsReasonValid(true)
        setNoteReason(e.target.textContent)
    }, [])

    const noop = useCallback(() => { }, [])


    useEffect(() => {
        const splitNote = (note?.longDescription ?? '').split(
            '<span contentEditable={false}>'
        )
        setLongDescription(`${splitNote?.[0]}`)
        if (!!splitNote?.[1]) {
            setPostFix(`<br/><span contentEditable={false}>${splitNote?.[1]}`)
        }
        setNoteReason(note?.reason)
    }, [])

    return (
        <Fragment>
            <Dialog
                key="AddNote"
                open={open}
                className={classes.dialog}
                maxWidth="xl"
                data-testid="addNotesModal"
                aria-labelledby="add-notes"
                PaperComponent={DraggablePaper}
            >
                {!!headerTitle && (
                    <DialogTitle className={clsx(classes.dialogTitle)}>
                        {headerTitle}
                    </DialogTitle>
                )}
                <DialogContent className={classes.dialogContent}>
                    <GridContainer
                        direction="column"
                        className={clsx(classes.elementContainer)}
                        spacing={2}
                    >
                        {!!action && reasonOptions?.length > 1 && (
                            <>
                                <GridItem
                                    xs={12}
                                    className={classes.reasonLabel}
                                >
                                    {noteReasonLabels[action]}
                                    <Typography
                                        component="span"
                                        className={classes.required}
                                    >
                                        {' '}
                                        *
                                    </Typography>
                                </GridItem>
                                <GridItem xs={12}>
                                    <AutoCompleteInput
                                        name="Select a reason"
                                        handleChange={handleSetReason} // TODO
                                        handleInputChange={noop}
                                        valid={isReasonValid}
                                        selectedValue={noteReason}
                                        items={reasonOptions}
                                        label=""
                                    />
                                </GridItem>
                            </>
                        )}
                        {!!action && (
                            <GridItem xs={12} className={classes.reasonLabel}>
                                {LABEL_REASON_NOTE}{' '}
                                <Typography
                                    component="span"
                                    className={classes.required}
                                >
                                    *
                                </Typography>
                            </GridItem>
                        )}
                        <GridItem xs={12}>
                            <EditableTextBlock
                                showCharCounter
                                data-testid="notes-text-editor"
                                title=""
                                disableTitleBar
                                editable
                                valid={!error}
                                maxChars={textLimit}
                                content={longDescription}
                                isLoading={false}
                                onChange={handleNoteInputChange}
                                contentEditableStyle={{
                                    maxHeight: '60vh',
                                    overflowY: 'auto',
                                }}
                            />
                        </GridItem>
                    </GridContainer>
                </DialogContent>
                <DialogActions className={classes.dialogAction}>
                    <Button
                        data-testid="cancel-add-pdn"
                        className={clsx(
                            classes.button,
                            classes.secondaryButton
                        )}
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={!longDescription.length || !!error}
                        data-testid="save-add-pdn"
                        className={clsx(classes.button, classes.primaryButton, {
                            [classes.disabledButton]:
                                !longDescription.length || !!error,
                        })}
                        onClick={handleSave}
                    >
                        {(!note?.isTemporary && !!note) ||
                            (!note && noteType === NoteType.GeneralNote)
                            ? 'Save'
                            : 'Ok'}
                    </Button>
                </DialogActions>
            </Dialog>
            {!!action && !!resetAction && (
                <ConfirmationDialog
                    openDialog={isBlockCancelOpen}
                    dialogHeading="Confirm"
                    contentNode={
                        cancelAddNoteWarning[
                        action as keyof typeof cancelAddNoteWarning
                        ]
                    }
                    handleDialogClose={handleBlockModalClose}
                    dialogActionCallback={handleBlockModalProceed}
                    size="small"
                    cancelText="Cancel"
                    confirmText="OK"
                />
            )}
        </Fragment>
    )
}
